import React from 'react';
import { CollaborativeChartsReviewItem, ProviderChartItemType } from '@uplift/api';
import { getAppointmentTitle } from '@uplift/ui-v2';
import { ProviderMedicalRole } from '@uplift/types';
import { SessionClinicalNotesReview } from '../../../components/patient-chart-review-modal/components/SessionClinicalNotesReview.component';
import { MedicalNoteReview } from './MedicalNoteReview';
import { Questionnaire } from './Questionare';
import { ChartsReviewLayout } from './ChartsReviewLayout';
import { Box, Typography } from '@mui/material';
import { CalendarDateService } from '@uplift/services';
import { medicalNoteReviewByIdQuery, useCollaborativeChartsReviewClinicalFlagsQuery, useSessionByAppointmentIdQuery } from '@uplift/queries';
import { ChartsReviewFlagButton } from './Flagging';
import { useTimezone } from '@uplift/contexts';
import { DateFormats } from '@uplift/constants';
import { useSuspenseQuery } from '@tanstack/react-query';


const ReviewBlockTitleMedicalNote: React.VFC<{ medicalNoteId: string }> = ({ medicalNoteId }) => {
  const timezone = useTimezone();
  const { data: note } = useSuspenseQuery(medicalNoteReviewByIdQuery(medicalNoteId));

  return <>Billable Note {CalendarDateService.date(note.submittedAt).tz(timezone).format(DateFormats['M/D/YYYY'])}</>;
};

const ReviewBlockTitleAppointment: React.VFC<{ appointmentId: string }> = ({ appointmentId }) => {
  const timezone = useTimezone();
  const { data: session } = useSessionByAppointmentIdQuery(appointmentId, { suspense: true });

  return <>{getAppointmentTitle(ProviderMedicalRole.psychiatrist, 60)} {CalendarDateService.date(session.submittedAt).tz(timezone).format(DateFormats['M/D/YYYY'])}</>;
};

export const ReviewBlockTitle: React.VFC<{ resourceType: ProviderChartItemType; resourceId: string }> = ({ resourceType, resourceId }) => {
  if (resourceType === ProviderChartItemType.billableNoteSubmitted) {
    return <ReviewBlockTitleMedicalNote medicalNoteId={resourceId} />;
  }
  if (resourceType === ProviderChartItemType.psychiatryAppointmentSubmitted) {
    return <ReviewBlockTitleAppointment appointmentId={resourceId} />;
  }

  return <>Unknown</>;
};


export const ReviewBlock: React.VFC<{
  chartsReviewItem: CollaborativeChartsReviewItem;
  onGoNext: () => void;
  disabled?: boolean;
}> = ({ chartsReviewItem, disabled, onGoNext }) => {
  const { data: clinicalReviewClinicalFlagByResource } = useCollaborativeChartsReviewClinicalFlagsQuery(chartsReviewItem.reviewId);
  const clinicalReviewFlag = clinicalReviewClinicalFlagByResource[chartsReviewItem.resourceId];
  const [ flagged, setFlagged ] = React.useState(!!clinicalReviewFlag);

  return (
    <ChartsReviewLayout
      title={(
        <ReviewBlockTitle
          resourceType={chartsReviewItem.resourceType}
          resourceId={chartsReviewItem.resourceId}
        />
      )}
      subtitle={(
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="textSecondary">saved {CalendarDateService.date(chartsReviewItem.updatedAt).fromNow()}</Typography>
          <ChartsReviewFlagButton flagged={flagged} setFlagged={setFlagged} />
        </Box>
      )}
      left={(
        <>
          {chartsReviewItem.resourceType === ProviderChartItemType.psychiatryAppointmentSubmitted && (
            <SessionClinicalNotesReview appointmentId={chartsReviewItem.resourceId} showPatientTitle={false} />
          )}
          {chartsReviewItem.resourceType === ProviderChartItemType.billableNoteSubmitted && (
            <MedicalNoteReview medicalNoteId={chartsReviewItem.resourceId} />
          )}
        </>
      )}
      right={(
        <Questionnaire
          chartsReviewItem={chartsReviewItem}
          disabled={disabled}
          flagged={flagged}
          setFlagged={setFlagged}
          onGoNext={onGoNext}
        />
      )}
    />
  );
};
